/**
 * Copyright 2023 AutoZone, Inc.
 * Content is confidential to and proprietary information of AutoZone, Inc., its
 * subsidiaries and affiliates.
 */
import styles from './CMSNullResult.module.scss';
import { type ContentStackNullResult } from '../../interface';
import {
  useProductShelfResults,
  useProduleShelfFacets,
} from '@/features/category/api/getProductShelfResults';
import {
  useProductShelfACESFacets,
  useProductShelfACESResults,
} from '@/features/category/api/getProductShelfACESResults';
import { Divider, Link, Text, View, classNames } from '@az/starc-ui';
import { useDeleteDefaultVehicleMutation } from '@/features/ymme/api/deleteDefaultVehicle';
import { CMSImage } from '../CMSImage/CMSImage';
import { useAcesFlags } from '@/features/ymme/utils/acesFlags';

type CMSNullCartidgeProps = {
  content: ContentStackNullResult;
};

const CMSNullCartidge = ({ content }: Pick<CMSNullCartidgeProps, 'content'>) => {
  const { background_image } = content;
  const { acesEnabled } = useAcesFlags();
  const useProductShelfFacetsFunction = acesEnabled
    ? useProductShelfACESFacets
    : useProduleShelfFacets;
  const { data: shelfData, isPending, isSuccess } = useProductShelfFacetsFunction();
  const { mutate: removeDefaultVehicle } = useDeleteDefaultVehicleMutation();

  if (isPending || !isSuccess) {
    return null;
  }

  const facet = shelfData?.productShelfResults?.facets?.find(
    (facet) => facet.facetInternalName === content.dimension_name,
  );
  if (!facet) {
    return null;
  }
  return (
    <View className={styles.container}>
      <View padding={[0, 4]}>
        <Divider />
      </View>
      <View direction={{ s: 'column', m: 'row' }} className={styles.innerContainer}>
        <View.Item grow>
          <Text
            className={classNames(styles.heading, styles.largeText)}
            as="h2"
            fontFamily="headline"
            weight="medium"
            textCase="uppercase"
          >
            {content.heading}
          </Text>
          <View gap={2} className={styles.lowerContainer}>
            <Text className={styles.mediumText} as="h3" weight="heavy">
              {content.sub_heading}
            </Text>
            <Text
              className={styles.smallText}
              attributes={{ dangerouslySetInnerHTML: { __html: content.description ?? '' } }}
            >
              {null}
            </Text>
          </View>
        </View.Item>
        <View.Item grow>
          <View
            className={styles.facetsContainer}
            direction="row"
            justify={{ s: 'center', m: 'start', l: 'start' }}
          >
            {facet.facetValues?.map(({ seoUrl, facetValueDisplayName, facetValueName }) => (
              <View.Item className={styles.facetContainer} key={facetValueName}>
                <Link onClick={() => removeDefaultVehicle()} href={seoUrl}>
                  <Text className={styles.smallText}>{facetValueDisplayName}</Text>
                </Link>
              </View.Item>
            ))}
          </View>
        </View.Item>
        {background_image?.image_url && (
          <CMSImage image={background_image} className={styles.backgroundImage} />
        )}
      </View>
    </View>
  );
};

export const CMSNullResult = ({ content }: CMSNullCartidgeProps) => {
  const { acesEnabled } = useAcesFlags();
  const useShelfResultsFunction = acesEnabled ? useProductShelfACESResults : useProductShelfResults;
  const { data: shelfData, status: productsShelfStatus } = useShelfResultsFunction();
  if (productsShelfStatus !== 'success') {
    return null;
  }
  if (shelfData?.productShelfResults?.totalNumberOfRecords === 0) {
    return <CMSNullCartidge content={content} />;
  }
  return null;
};
